import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { RowData } from "@tanstack/react-table";
import type { Column, ReactTable } from "@/lib/Components/DataTable/types";
import {
  CheckIcon,
  GripVerticalIcon,
  SlidersHorizontalIcon,
} from "lucide-react";
import { useTranslations } from "use-intl";

import { Button } from "@/components/catalyst/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface DataTableColumnOptionsProps<TData extends RowData> {
  table: ReactTable<TData>;
  disabled?: boolean;
  onReset?: () => void;
}

export function DataTableColumnOptions<TData extends RowData>({
  table,
  disabled = false,
  onReset,
}: DataTableColumnOptionsProps<TData>) {
  "use no memo";

  const t = useTranslations("common.dataTable");

  const sensors = useSensors(
    // A small distance keeps a click on the handle from starting a drag, so the
    // handle can still be tabbed to and activated by keyboard.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const leafColumns = table.getAllLeafColumns();
  const toggleableColumns = leafColumns.filter((column) => column.getCanHide());
  const toggleableIds = toggleableColumns.map((column) => column.id);

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) {
      return;
    }

    const from = toggleableIds.indexOf(String(active.id));
    const to = toggleableIds.indexOf(String(over.id));

    if (from === -1 || to === -1) {
      return;
    }

    const reordered = arrayMove(toggleableIds, from, to);

    // Columns that can't be hidden (row select, expand chevron) aren't in the
    // menu, so they keep their slot while the toggleable ones move around them.
    let next = 0;
    table.setColumnOrder(
      leafColumns.map((column) =>
        column.getCanHide() ? reordered[next++] : column.id,
      ),
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          outline
          className="ml-auto hidden h-9 lg:flex"
          disabled={disabled}
        >
          <SlidersHorizontalIcon className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-56">
        <DropdownMenuLabel>{t("toggleColumns")}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div className="max-h-80 overflow-y-auto py-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={toggleableIds}
              strategy={verticalListSortingStrategy}
            >
              {toggleableColumns.map((column) => (
                <ColumnOption
                  key={column.id}
                  column={column}
                  dragLabel={t("reorderColumn")}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        {onReset ? (
          <>
            <DropdownMenuSeparator />
            <button
              type="button"
              className="w-full rounded-sm px-2 py-1.5 text-left text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              onClick={onReset}
            >
              {t("resetColumns")}
            </button>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function ColumnOption<TData extends RowData>({
  column,
  dragLabel,
}: {
  column: Column<TData, unknown>;
  dragLabel: string;
}) {
  "use no memo";

  const {
    attributes,
    isDragging,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: column.id });

  const isVisible = column.getIsVisible();
  const label = getColumnLabel(column);

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={cn(
        "flex items-center gap-1 rounded-sm pl-1 pr-2",
        isDragging && "relative z-10 bg-white shadow-sm",
      )}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        aria-label={`${dragLabel}: ${label}`}
        className="cursor-grab touch-none rounded p-1 text-gray-400 hover:text-gray-600 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVerticalIcon className="h-4 w-4" />
      </button>

      <button
        type="button"
        aria-pressed={isVisible}
        onClick={() => column.toggleVisibility(!isVisible)}
        className="flex flex-1 items-center gap-2 rounded-sm py-1.5 text-left text-sm hover:bg-gray-50"
      >
        <CheckIcon
          className={cn("h-4 w-4 shrink-0", !isVisible && "invisible")}
        />
        <span className="truncate">{label}</span>
      </button>
    </div>
  );
}

function getColumnLabel<TData extends RowData>(
  column: Column<TData, unknown>,
): string {
  const { label } = column.columnDef as { label?: string };

  if (label) {
    return label;
  }

  return column.id
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}
